import { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ApiError } from '@/lib/api';
import { roomsApi, type DiscoveredRoom, type RoomFilters } from '@/lib/rooms';
import {
  mappingsApi,
  BULK_BATCH_SIZE,
  type BulkResult,
  type Mapping,
  type Pod,
  type Suggestion,
} from '@/lib/mappings';
import {
  Button,
  Card,
  CONTROL,
  FilterField,
  LoadingLine,
  Notice,
  PageHeading,
  Refreshing,
  SkeletonRows,
  StatusChip,
  TextButton,
  TogglePill,
} from '@/components/ui';
import { ConfirmDialog } from '@/components/ConfirmDialog';

/**
 * Screen 6 — Space mapping. The screen that matters most.
 *
 * Constraints it is built to, all from the brief:
 *   - usable at 500 rows: filtering, paging, bulk apply, full keyboard support;
 *   - suggestions pre-filled and CLEARLY MARKED as suggestions, never as facts;
 *   - accept all, then one apply with a count;
 *   - filter by building, floor, and unmapped only.
 *
 * Two decisions worth stating. Pending choices live in a `Map` keyed by pod, so
 * a filter change never discards what someone has already picked — losing 40
 * selections because they typed in the search box is the fastest way to make a
 * screen untrusted. And apply goes in batches with visible progress, because
 * the server serialises each row on purpose and a silent two-minute wait reads
 * as a hang.
 */

interface PodRow {
  meeting_space_id: string;
  meeting_space_name: string;
  /** Shown under the name, because pod names repeat across groups. */
  space_group_name: string | null;
  mapping: Mapping | null;
}

export function Mappings() {
  const [rooms, setRooms] = useState<DiscoveredRoom[]>([]);
  const [podList, setPodList] = useState<Pod[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [filters, setFilters] = useState<RoomFilters | null>(null);
  const [suggestions, setSuggestions] = useState<Map<string, Suggestion>>(new Map());
  const [pending, setPending] = useState<Map<string, string>>(new Map());

  /**
   * `loading` is the FIRST read only. Later reads — after an apply, after an
   * unlink — set `refreshing`, which dims the rows rather than replacing the
   * table with a skeleton and losing the reader's place.
   */
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  /** The pod whose unlink is being confirmed, and whether the request is in flight. */
  const [unlinking, setUnlinking] = useState<PodRow | null>(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);

  const [building, setBuilding] = useState('');
  const [floor, setFloor] = useState('');
  const [search, setSearch] = useState('');
  const [unmappedOnly, setUnmappedOnly] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Pods are loaded HERE, not by the suggestion call. Pressing a button
      // to find out whether you have any pods at all was the old behaviour and
      // it made a screen with nothing on it look like a broken integration.
      const [roomPage, filterOptions, mappingList, pods] = await Promise.all([
        roomsApi.list({ limit: 500 }),
        roomsApi.filters(),
        mappingsApi.list(),
        mappingsApi.pods(),
      ]);
      setRooms(roomPage.data);
      setFilters(filterOptions);
      setMappings(mappingList);
      setPodList(pods);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not load the mapping screen.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Every pod, whether or not anything matched it.
   *
   * `/calendar/pods` is the source. Mappings are merged in for the link, and a
   * mapped pod that the pod list somehow missed still gets a row rather than
   * disappearing — a link that exists must always be visible, because it is
   * the only way to remove it.
   */
  const pods: PodRow[] = useMemo(() => {
    const byId = new Map<string, PodRow>();
    const mappingBySpace = new Map(mappings.map((m) => [m.meeting_space_id, m]));

    for (const pod of podList) {
      byId.set(pod.meeting_space_id, {
        meeting_space_id: pod.meeting_space_id,
        meeting_space_name: pod.meeting_space_name || 'Pod',
        space_group_name: pod.space_group_name,
        mapping: mappingBySpace.get(pod.meeting_space_id) ?? null,
      });
    }
    for (const mapping of mappings) {
      if (byId.has(mapping.meeting_space_id)) continue;
      byId.set(mapping.meeting_space_id, {
        meeting_space_id: mapping.meeting_space_id,
        meeting_space_name: mapping.meeting_space_name ?? 'Pod',
        // A mapped pod the pod list did not return has no group to show; the
        // row still has to exist, because it is the only way to remove it.
        space_group_name: null,
        mapping,
      });
    }
    // Grouped first, then by name: two "ZenPod 1" rows sitting next to each
    // other with different groups above them is the whole point of showing the
    // group at all.
    return [...byId.values()].sort(
      (a, b) =>
        (a.space_group_name ?? '').localeCompare(b.space_group_name ?? '') ||
        a.meeting_space_name.localeCompare(b.meeting_space_name),
    );
  }, [podList, mappings]);

  const roomsById = useMemo(() => new Map(rooms.map((r) => [r.id, r])), [rooms]);
  const linkedCount = mappings.length;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return pods.filter((pod) => {
      if (unmappedOnly && pod.mapping) return false;
      // The group is searchable too — with repeated pod names it is often the
      // only thing that narrows a list.
      if (
        needle &&
        !pod.meeting_space_name.toLowerCase().includes(needle) &&
        !(pod.space_group_name ?? '').toLowerCase().includes(needle)
      ) {
        return false;
      }
      if (building || floor) {
        const roomId = pending.get(pod.meeting_space_id) ?? pod.mapping?.discovered_room_id;
        const room = roomId ? roomsById.get(roomId) : undefined;
        if (building && room?.building !== building) return false;
        if (floor && room?.floor !== floor) return false;
      }
      return true;
    });
  }, [pods, search, unmappedOnly, building, floor, pending, roomsById]);

  const availableRooms = useMemo(() => {
    const taken = new Set([
      ...mappings.map((m) => m.discovered_room_id),
      ...pending.values(),
    ]);
    return rooms.filter((room) => room.is_active && !taken.has(room.id));
  }, [rooms, mappings, pending]);

  async function loadSuggestions() {
    try {
      const found = await mappingsApi.suggest();
      setSuggestions(new Map(found.map((s) => [s.meeting_space_id, s])));
      if (found.length === 0) {
        // The rows are already on screen, so this is information rather than a
        // dead end — which is exactly what it was when the rows came from here.
        setError(
          pods.length === 0
            ? 'No pods found for this organization. Check your ZenSpace access.'
            : 'No confident name matches — pick a room for each pod below.',
        );
      } else {
        setError(null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not work out any suggestions.');
    }
  }

  function acceptAllSuggestions() {
    const next = new Map(pending);
    for (const suggestion of suggestions.values()) {
      if (mappings.some((m) => m.meeting_space_id === suggestion.meeting_space_id)) continue;
      next.set(suggestion.meeting_space_id, suggestion.room_id);
    }
    setPending(next);
  }

  function choose(meetingSpaceId: string, roomId: string) {
    const next = new Map(pending);
    if (roomId) next.set(meetingSpaceId, roomId);
    else next.delete(meetingSpaceId);
    setPending(next);
  }

  /**
   * Apply in batches, with progress.
   *
   * Re-running is safe by design, so a batch that fails is not a dead end — the
   * message says so, because "12 could not be linked" without that reads as
   * data loss.
   */
  async function apply() {
    const entries = [...pending.entries()].map(([meeting_space_id, room_id]) => ({
      meeting_space_id,
      meeting_space_name: pods.find((p) => p.meeting_space_id === meeting_space_id)
        ?.meeting_space_name,
      room_id,
    }));
    if (entries.length === 0) return;

    setProgress({ done: 0, total: entries.length });
    setResult(null);
    const combined: BulkResult = {
      requested: 0,
      created: 0,
      already_mapped: 0,
      resumed: 0,
      rejected: 0,
      results: [],
    };

    try {
      for (let i = 0; i < entries.length; i += BULK_BATCH_SIZE) {
        const batch = entries.slice(i, i + BULK_BATCH_SIZE);
        const batchResult = await mappingsApi.applyBatch(batch);
        combined.requested += batchResult.requested;
        combined.created += batchResult.created;
        combined.already_mapped += batchResult.already_mapped;
        combined.resumed += batchResult.resumed;
        combined.rejected += batchResult.rejected;
        combined.results.push(...batchResult.results);
        setProgress({ done: Math.min(i + BULK_BATCH_SIZE, entries.length), total: entries.length });
      }
      setResult(combined);
      setPending(new Map());
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'We could not finish applying. Nothing is linked twice — applying again will pick up where this left off.',
      );
    } finally {
      setProgress(null);
    }
  }

  /**
   * Unlinking is confirmed in our own dialog, not the browser's.
   *
   * The names matter here: this screen is about one pod and one room, and
   * `window.confirm` cannot show either — it renders the ORIGIN as its heading
   * and a single line of text under it. The dialog also stays up while the
   * request runs, because unlinking stops two subscriptions and is not instant.
   */
  async function confirmUnlink() {
    const mapping = unlinking?.mapping;
    if (!mapping) return;

    setUnlinkBusy(true);
    try {
      await mappingsApi.remove(mapping.id);
      setUnlinking(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not unlink that pod.');
      setUnlinking(null);
    } finally {
      setUnlinkBusy(false);
    }
  }

  const suggestedUnapplied = [...suggestions.values()].filter(
    (s) => !mappings.some((m) => m.meeting_space_id === s.meeting_space_id),
  ).length;

  return (
    <div className="mx-auto w-full max-w-[1200px] px-[28px] py-[40px]">
      <div className="mb-[28px]">
        <PageHeading
          eyebrow="Calendar"
          title="Space mapping"
          sub="Link each pod to the room it is in your calendar. One pod to one room, both ways."
          actions={
            <div className="flex flex-wrap gap-[8px]">
              <Button tone="quiet" onClick={loadSuggestions}>
                Suggest matches
              </Button>
              {suggestedUnapplied > 0 && (
                <Button tone="quiet" onClick={acceptAllSuggestions}>
                  Accept all {suggestedUnapplied}
                </Button>
              )}
              <Button
                tone="primary"
                disabled={pending.size === 0 || progress !== null}
                busy={progress !== null}
                onClick={apply}
              >
                {progress
                  ? `Applying ${progress.done} of ${progress.total}…`
                  : `Apply ${pending.size || ''}`.trim()}
              </Button>
            </div>
          }
        />
      </div>

      {error && (
        <div className="mb-[20px]">
          <Notice tone="attention" title={error} />
        </div>
      )}

      {result && (
        <div className="mb-[20px]">
          <Notice
            tone={result.rejected === 0 ? 'healthy' : 'attention'}
            title={
              result.rejected === 0
                ? `${result.created + result.resumed + result.already_mapped} pod(s) linked.`
                : `${result.created + result.resumed} linked, ${result.rejected} could not be.`
            }
          >
            {result.rejected > 0 && (
              <>
                <p className="m-0 mb-[8px]">
                  Nothing was linked twice. Applying again picks up exactly what is missing.
                </p>
                <ul className="m-0 flex list-none flex-col gap-[4px] p-0">
                  {result.results
                    .filter((r) => r.status === 'rejected')
                    .slice(0, 10)
                    .map((r) => (
                      <li key={r.meetingSpaceId} className="text-body">
                        {r.message}
                      </li>
                    ))}
                </ul>
              </>
            )}
          </Notice>
        </div>
      )}

      <Card className="mb-[20px]">
        <div className="flex flex-wrap items-end gap-[14px]">
          <FilterField label="Search pods" grow>
            <input
              type="search"
              value={search}
              placeholder="Pod or group name"
              onChange={(e) => setSearch(e.target.value)}
              className={CONTROL}
            />
          </FilterField>
          <FilterField label="Building">
            <select value={building} onChange={(e) => setBuilding(e.target.value)} className={CONTROL}>
              <option value="">All</option>
              {(filters?.buildings ?? []).map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Floor">
            <select value={floor} onChange={(e) => setFloor(e.target.value)} className={CONTROL}>
              <option value="">All</option>
              {(filters?.floors ?? []).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </FilterField>
          <TogglePill checked={unmappedOnly} onChange={setUnmappedOnly}>
            Not linked yet
          </TogglePill>
          {refreshing && !loading && (
            <span className="ml-auto">
              <LoadingLine>Updating…</LoadingLine>
            </span>
          )}
          <span className={clsx('text-sub text-muted', !(refreshing && !loading) && 'ml-auto')}>
            {visible.length === pods.length
              ? `${pods.length} pods`
              : `${visible.length} of ${pods.length} pods`}
            {linkedCount > 0 && ` · ${linkedCount} linked`}
          </span>
        </div>
      </Card>

      {loading ? (
        <SkeletonRows rows={5} label="Loading your pods…" />
      ) : pods.length === 0 ? (
        <Notice tone="neutral" title="Nothing to map yet">
          Press <strong>Suggest matches</strong> to load your pods and see which rooms they might
          be. If nothing appears, connect a calendar first and re-read the directory.
        </Notice>
      ) : (
        <Refreshing busy={refreshing}>
        <Card padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-body">
              <caption className="zs-sr-only">
                Pods and the calendar room each is linked to. Use the select in each row to choose
                a room, then Apply.
              </caption>
              <thead>
                <tr className="border-b border-divider text-left">
                  <th className="zs-eyebrow px-[18px] py-[14px] text-muted">Pod</th>
                  <th className="zs-eyebrow px-[18px] py-[14px] text-muted">Room</th>
                  <th className="zs-eyebrow px-[18px] py-[14px] text-muted">Capacity</th>
                  <th className="zs-eyebrow px-[18px] py-[14px] text-muted">Status</th>
                  <th className="zs-eyebrow px-[18px] py-[14px]">
                    <span className="zs-sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-[18px] py-[28px] text-center text-body text-muted">
                      No pods match these filters. Anything you have already chosen is kept.
                    </td>
                  </tr>
                )}
                {visible.map((pod) => (
                  <PodMappingRow
                    key={pod.meeting_space_id}
                    pod={pod}
                    suggestion={suggestions.get(pod.meeting_space_id) ?? null}
                    pendingRoomId={pending.get(pod.meeting_space_id) ?? null}
                    rooms={availableRooms}
                    roomsById={roomsById}
                    onChoose={(roomId) => choose(pod.meeting_space_id, roomId)}
                    onUnlink={() => setUnlinking(pod)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        </Refreshing>
      )}

      <ConfirmDialog
        open={unlinking !== null}
        title="Unlink this pod?"
        confirmLabel="Unlink pod"
        cancelLabel="Keep the link"
        busy={unlinkBusy}
        onConfirm={() => void confirmUnlink()}
        onCancel={() => !unlinkBusy && setUnlinking(null)}
      >
        {unlinking && (
          <>
            {/* The two names, because this is the one thing the browser's own
                confirm could not show — and picking the wrong row is the
                mistake worth preventing. */}
            <p className="m-0 flex flex-wrap items-center gap-[8px] text-body text-ink">
              <strong className="font-semibold">{unlinking.meeting_space_name}</strong>
              <span aria-hidden="true" className="text-muted">
                →
              </span>
              <span>
                {roomsById.get(unlinking.mapping?.discovered_room_id ?? '')?.name ??
                  unlinking.mapping?.room_calendar_id}
              </span>
            </p>
            <ul className="mt-[14px] mb-0 flex list-none flex-col gap-[8px] p-0">
              <li className="flex gap-[8px]">
                <span aria-hidden="true">✓</span>
                Meetings already in the calendar stay exactly where they are.
              </li>
              <li className="flex gap-[8px]">
                <span aria-hidden="true">–</span>
                New bookings stop syncing between this pod and that room.
              </li>
              <li className="flex gap-[8px]">
                <span aria-hidden="true">↺</span>
                You can link them again at any time.
              </li>
            </ul>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}

function PodMappingRow({
  pod,
  suggestion,
  pendingRoomId,
  rooms,
  roomsById,
  onChoose,
  onUnlink,
}: {
  pod: PodRow;
  suggestion: Suggestion | null;
  pendingRoomId: string | null;
  rooms: DiscoveredRoom[];
  roomsById: Map<string, DiscoveredRoom>;
  onChoose: (roomId: string) => void;
  onUnlink: () => void;
}) {
  const linkedRoom = pod.mapping ? roomsById.get(pod.mapping.discovered_room_id) : undefined;
  const chosenRoom = pendingRoomId ? roomsById.get(pendingRoomId) : undefined;
  const selectId = `room-for-${pod.meeting_space_id}`;

  // The room a row can pick: everything still free, plus whatever this row has
  // already chosen or is already linked to.
  const options = [...rooms];
  if (chosenRoom && !options.some((r) => r.id === chosenRoom.id)) options.unshift(chosenRoom);

  return (
    <tr
      className={clsx(
        'border-b border-divider transition-colors last:border-0 hover:bg-page',
        // A row with an unapplied choice is tinted, so what Apply will act on
        // is visible without reading every select on the screen.
        pendingRoomId && 'bg-sky-tint/20',
      )}
    >
      <td className="px-[18px] py-[14px] align-top">
        <span className="block font-medium text-ink">{pod.meeting_space_name}</span>
        {pod.space_group_name && (
          <span className="mt-[2px] block text-caption text-muted">{pod.space_group_name}</span>
        )}
      </td>

      <td className="px-[18px] py-[14px] align-top">
        {pod.mapping ? (
          <span className="block text-ink">{linkedRoom?.name ?? pod.mapping.room_calendar_id}</span>
        ) : (
          <>
            <label className="zs-sr-only" htmlFor={selectId}>
              Room for {pod.meeting_space_name}
            </label>
            <select
              id={selectId}
              value={pendingRoomId ?? ''}
              onChange={(e) => onChoose(e.target.value)}
              className={clsx(CONTROL, 'w-full max-w-[300px] py-[8px]')}
            >
              <option value="">Not linked</option>
              {options.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name}
                  {room.floor ? ` · floor ${room.floor}` : ''}
                </option>
              ))}
            </select>
            {/* Marked as a suggestion, every time. It is not a fact, and a
                wrong mapping sends real bookings to the wrong room. */}
            {suggestion && !pendingRoomId && (
              <button
                type="button"
                onClick={() => onChoose(suggestion.room_id)}
                className="mt-[6px] block border-0 bg-transparent p-0 text-left text-caption text-brand underline underline-offset-2"
              >
                Suggested: {suggestion.room_name} — {suggestion.reason}
              </button>
            )}
            {suggestion && pendingRoomId === suggestion.room_id && (
              <span className="mt-[6px] block text-caption text-muted">
                From a suggestion — {suggestion.reason}
              </span>
            )}
          </>
        )}
      </td>

      <td className="px-[18px] py-[14px] align-top text-right text-muted tabular-nums">
        {(chosenRoom ?? linkedRoom)?.capacity ?? '—'}
      </td>

      <td className="px-[18px] py-[14px] align-top">
        {pod.mapping ? (
          pod.mapping.registration_state === 'active' ? (
            <StatusChip tone="healthy">Syncing</StatusChip>
          ) : (
            <StatusChip tone="attention">Linked, not syncing yet</StatusChip>
          )
        ) : pendingRoomId ? (
          <StatusChip tone="neutral">Will be linked</StatusChip>
        ) : (
          <StatusChip tone="neutral">Not linked</StatusChip>
        )}
      </td>

      <td className="px-[18px] py-[14px] align-top text-right">
        {pod.mapping && (
          <TextButton
            onClick={onUnlink}
            aria-label={`Unlink ${pod.meeting_space_name}`}
            className="text-danger"
          >
            Unlink
          </TextButton>
        )}
      </td>
    </tr>
  );
}
